/**
 * 基础语言状态钩子
 * 提供React组件使用的语言状态管理接口
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import LanguageStateManager from '../state/index.js';
import { getSourceLanguages, getTargetLanguages } from '@readsavor/utils';
/**
 * 使用语言状态钩子
 * 提供全局语言状态管理
 * @param modelId 可选的模型ID，用于获取该模型支持的语言
 * @returns 语言状态和相关方法
 */
export function useLanguageState(modelId) {
    const manager = LanguageStateManager.getInstance();
    // 状态初始化
    const [sourceLanguage, setSourceLanguageState] = useState(manager.getPreferredSourceLanguage());
    const [targetLanguage, setTargetLanguageState] = useState(manager.getPreferredTargetLanguage());
    // 订阅语言变化
    useEffect(() => {
        const unsubscribeSource = manager.subscribeToSourceLanguage(newLang => setSourceLanguageState(newLang));
        const unsubscribeTarget = manager.subscribeToTargetLanguage(newLang => setTargetLanguageState(newLang));
        return () => {
            unsubscribeSource();
            unsubscribeTarget();
        };
    }, [manager]);
    // 更新首选源语言
    const setSourceLanguage = useCallback((lang) => {
        manager.setPreferredSourceLanguage(lang);
    }, [manager]);
    // 更新首选目标语言
    const setTargetLanguage = useCallback((lang) => {
        manager.setPreferredTargetLanguage(lang);
    }, [manager]);
    // 增强版更新源语言方法（包含数据库同步）
    const setSourceLanguageWithSync = useCallback(async (lang, docId) => {
        return await manager.setSourceLanguageWithSync(lang, docId);
    }, [manager]);
    // 增强版更新目标语言方法（包含数据库同步）
    const setTargetLanguageWithSync = useCallback(async (lang, docId) => {
        return await manager.setTargetLanguageWithSync(lang, docId);
    }, [manager]);
    // 设置当前文档ID并加载其语言设置
    const setCurrentDocument = useCallback((identity) => {
        return manager.setCurrentDocument(identity);
    }, [manager]);
    // 获取当前活动的文档身份标识符
    const getCurrentDocumentIdentity = useCallback(() => {
        return manager.getCurrentDocumentIdentity();
    }, [manager]);
    // 缓存语言列表，根据modelId获取相应的语言列表
    const sourceLanguages = useMemo(() => getSourceLanguages(modelId), [modelId]);
    const targetLanguages = useMemo(() => getTargetLanguages(modelId), [modelId]);
    /**
     * 显式应用从已加载文档数据中获取的语言设置。
     * 这是新架构下推荐的、用于同步特定文档语言到全局状态的方式。
     *
     * @param identity 当前文档的身份标识符。
     * @param sourceLang 从文档数据中加载的源语言。
     * @param targetLang 从文档数据中加载的目标语言。
     */
    const applyLanguageSettingsFromLoadedDocument = useCallback((identity, sourceLang, targetLang) => {
        manager.applyLanguageSettingsFromLoadedDocument(identity, sourceLang, targetLang);
    }, [manager]);
    // New method for updating user preferred target language and persisting it
    const updateUserPreferredTargetLanguage = useCallback(async (identity, newLang) => {
        const logPrefix = '[useLanguageState updateUserPreferredTargetLanguage]';
        try {
            await manager.updateUserPreferredTargetLanguage(identity, newLang);
            // No explicit return needed if LanguageStateManager method returns void on success
            // or re-throw if it throws an error, which it does.
        }
        catch (error) {
            console.error(`${logPrefix} Error for doc ID ${identity.id}, new language "${newLang}":`, error);
            throw error; // Rethrow to allow calling component to handle UI feedback
        }
    }, [manager]);
    return {
        sourceLanguage,
        targetLanguage,
        setSourceLanguage,
        setTargetLanguage,
        setSourceLanguageWithSync,
        setTargetLanguageWithSync,
        isValidPair: (src, tgt) => tgt !== 'auto',
        sourceLanguages,
        targetLanguages,
        // 文档相关方法
        setCurrentDocument,
        getCurrentDocumentIdentity,
        applyLanguageSettingsFromLoadedDocument,
        updateUserPreferredTargetLanguage,
    };
}
//# sourceMappingURL=useLanguageState.js.map